import { useEffect, useRef, useState } from 'react'
import { Copy, Check, Upload, CreditCard, Clock, CheckCircle2, XCircle, FileText } from 'lucide-react'
import toast from 'react-hot-toast'
import { storePaymentsAPI } from '../services/api'
import { t } from '../i18n'

// What the customer still has to do to pay for an order paid online:
// bank transfer -> the store's bank details + upload the receipt;
// card -> a button to (re)open Stripe's payment page.
// Used on the checkout confirmation and the order tracking page.
export function BankDetails({ bank, reference }) {
  const [copied, setCopied] = useState('')
  if (!bank) return null
  const copy = (label, value) => {
    navigator.clipboard?.writeText(value).then(() => { setCopied(label); setTimeout(() => setCopied(''), 1500) }).catch(() => {})
  }
  const rows = [
    ['Account holder', bank.accountName], ['Bank', bank.bankName], ['Account number', bank.accountNumber],
    ['IBAN', bank.iban], ['SWIFT / BIC', bank.swift], ['Reference', reference],
  ].filter(([, v]) => v)
  return (
    <div className="sk-soft p-4">
      <dl className="bank-details">
        {rows.map(([label, value]) => (
          <div key={label}>
            <dt>{t(label)}</dt>
            <dd>{value}
              <button type="button" onClick={() => copy(label, value)} aria-label={t("Copy {label}", { label: label })} className="sk-muted hover:opacity-80">
                {copied === label ? <Check size={15} /> : <Copy size={15} />}
              </button>
            </dd>
          </div>
        ))}
      </dl>
      {bank.instructions && <p className="text-sm sk-muted mt-3">{bank.instructions}</p>}
    </div>
  )
}

export default function OrderPaymentPanel({ order, onUpdated }) {
  const [bank, setBank] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [startingCard, setStartingCard] = useState(false)
  const [proofStatus, setProofStatus] = useState(order.payment_proof_status || null)
  const fileInput = useRef(null)
  const method = order.payment_method
  const paid = order.payment_status === 'PAID'

  useEffect(() => { setProofStatus(order.payment_proof_status || null) }, [order.payment_proof_status])
  useEffect(() => {
    if (method === 'bank_transfer' && !paid) storePaymentsAPI.getPublic().then(({ data }) => setBank(data.bank_transfer)).catch(() => {})
  }, [method, paid])

  if (!['bank_transfer', 'card'].includes(method) || order.status === 'CANCELLED') return null

  if (paid) {
    return (
      <section className="sk-card flex gap-3 items-center">
        <CheckCircle2 className="text-emerald-600 flex-shrink-0" aria-hidden="true" />
        <div><p className="font-semibold">{t("Payment received")}</p><p className="text-sm sk-muted">{method === 'card' ? t("Your card payment went through.") : t("The store verified your bank transfer.")}</p></div>
      </section>
    )
  }

  if (method === 'card') {
    const payNow = async () => {
      setStartingCard(true)
      try {
        const { data } = await storePaymentsAPI.startCardCheckout(order.pin)
        window.location.href = data.url
      } catch (err) {
        toast.error(t(err.response?.data?.error || 'Could not open the payment page'))
        setStartingCard(false)
      }
    }
    return (
      <section className="sk-card">
        <h2 className="text-lg font-semibold flex items-center gap-2"><CreditCard size={19} /> {t("Pay by card")}</h2>
        <p className="text-sm sk-muted mt-1">{t("Your order is waiting for payment. You'll pay on Stripe's secure page.")}</p>
        <button onClick={payNow} disabled={startingCard} className="sk-btn w-full mt-5">{startingCard ? t("Opening secure payment…") : t("Pay now")}</button>
      </section>
    )
  }

  const upload = async file => {
    if (!file) return
    if (file.size > 8 * 1024 * 1024) return toast.error(t("The file is too large (max 8 MB)"))
    setUploading(true)
    try {
      const { data } = await storePaymentsAPI.uploadProof(order.pin, file)
      setProofStatus(data.payment_proof_status)
      toast.success(t("Receipt sent. The store will verify it shortly."))
      onUpdated?.()
    } catch (err) {
      toast.error(t(err.response?.data?.error || 'Upload failed. Please try again.'))
    } finally {
      setUploading(false)
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  const status = {
    pending: [Clock, 'Receipt received - waiting for the store to verify it', 'text-amber-600'],
    rejected: [XCircle, 'The store could not verify your receipt', 'text-red-600'],
  }[proofStatus]
  const StatusIcon = status?.[0]

  return (
    <section className="sk-card space-y-4">
      <div>
        <h2 className="text-lg font-semibold">{t("Pay by bank transfer")}</h2>
        <p className="text-sm sk-muted mt-1">{t("Transfer the total to the account below, then upload a photo or screenshot of the receipt.")}</p>
      </div>
      {order.total != null && <p className="flex justify-between font-semibold"><span>{t("Amount to transfer")}</span><span>{Number(order.total).toFixed(2)} {order.currency || ''}</span></p>}
      <BankDetails bank={bank} reference={order.order_code} />
      {status && (
        <div className={`flex gap-2 items-start text-sm font-medium ${status[2]}`} role="status">
          <StatusIcon size={18} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
          <div>{status[1]}{proofStatus === 'rejected' && order.payment_proof_note && <p className="font-normal sk-muted mt-1">&ldquo;{order.payment_proof_note}&rdquo;</p>}</div>
        </div>
      )}
      <label className="proof-drop">
        {uploading ? <p className="font-medium">{t("Uploading…")}</p> : <>
          {proofStatus ? <FileText aria-hidden="true" /> : <Upload aria-hidden="true" />}
          <span className="font-semibold">{proofStatus ? t("Upload a different receipt") : t("Upload transfer receipt")}</span>
          <span className="text-xs sk-muted">{t("JPG, PNG, WEBP or PDF · max 8 MB")}</span>
        </>}
        <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="sr-only" disabled={uploading} onChange={e => upload(e.target.files?.[0])} />
      </label>
    </section>
  )
}
